import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  useFieldArray,
  useForm,
  useWatch,
  type Resolver,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  type AudienceChannel,
  type CampaignFilterField,
  type CampaignFilterFieldDefinition,
  type CampaignFilterInput,
  campaignFilterDefinition,
  campaignFilterFields,
  filterFamilies,
} from '@/features/campaign-targeting/contracts';
import {
  type CampaignFilterForm,
  type CampaignFilterFormRule,
  campaignFilterSummary,
  emptyCampaignFilter,
  filterEditorSchema,
  toApiFilter,
} from '@/features/campaign-targeting/filter-schema';

interface CampaignFilterEditorProps {
  channels: readonly AudienceChannel[];
  value?: CampaignFilterForm;
  onChange: (filter: CampaignFilterInput) => void;
  onValidityChange: (valid: boolean) => void;
  onPreviewInvalidated: () => void;
  disabled?: boolean;
}

const defaultRule = (): CampaignFilterFormRule => ({
  field: '',
  operator: '',
  values: [],
});

export function CampaignFilterEditor({
  channels,
  value = emptyCampaignFilter,
  onChange,
  onValidityChange,
  onPreviewInvalidated,
  disabled = false,
}: CampaignFilterEditorProps) {
  const channelKey = [...new Set(channels)].sort().join('|');
  const externalValueKey = formSemanticKey(value);
  const callbacksRef = useRef({
    onChange,
    onValidityChange,
    onPreviewInvalidated,
  });
  callbacksRef.current = { onChange, onValidityChange, onPreviewInvalidated };
  const appliedExternalValueKeyRef = useRef(externalValueKey);
  const userEditGenerationRef = useRef(0);
  const handledUserEditGenerationRef = useRef(0);
  const hasObservedStateRef = useRef(false);
  const lastObservedStateKeyRef = useRef<string | undefined>(undefined);
  const lastValidityRef = useRef<boolean | undefined>(undefined);
  const schemaChannels = useMemo(
    () => (channelKey ? (channelKey.split('|') as AudienceChannel[]) : []),
    [channelKey],
  );
  const schema = useMemo(
    () => filterEditorSchema(schemaChannels),
    [schemaChannels],
  );
  const form = useForm<CampaignFilterForm>({
    resolver: zodResolver(schema) as Resolver<CampaignFilterForm>,
    defaultValues: value,
    mode: 'onChange',
  });
  const { control, formState, reset, setValue } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'rules' });
  const watchedRules = useWatch({ control, name: 'rules' });
  const rules = useMemo(() => watchedRules ?? [], [watchedRules]);
  const semanticFormKey = formSemanticKey({ rules });

  useLayoutEffect(() => {
    if (appliedExternalValueKeyRef.current === externalValueKey) return;
    appliedExternalValueKeyRef.current = externalValueKey;
    reset(value);
  }, [externalValueKey, reset, value]);

  useEffect(() => {
    const result = schema.safeParse({ rules });
    const filter = result.success
      ? toApiFilter(result.data.rules as CampaignFilterFormRule[])
      : undefined;
    const observedStateKey = JSON.stringify({
      channels: channelKey,
      valid: result.success,
      filter: filter ?? semanticFormKey,
    });
    if (lastObservedStateKeyRef.current === observedStateKey) return;
    lastObservedStateKeyRef.current = observedStateKey;

    const isInitialObservation = !hasObservedStateRef.current;
    hasObservedStateRef.current = true;
    const isUserEdit =
      userEditGenerationRef.current !== handledUserEditGenerationRef.current;
    handledUserEditGenerationRef.current = userEditGenerationRef.current;

    if (isInitialObservation) {
      callbacksRef.current.onValidityChange(result.success);
      if (filter) callbacksRef.current.onChange(filter);
    } else if (isUserEdit) {
      callbacksRef.current.onValidityChange(result.success);
      if (filter) callbacksRef.current.onChange(filter);
      callbacksRef.current.onPreviewInvalidated();
    } else if (lastValidityRef.current !== result.success) {
      callbacksRef.current.onValidityChange(result.success);
    }
    lastValidityRef.current = result.success;
  }, [channelKey, rules, schema, semanticFormKey]);

  const markUserEdit = () => {
    userEditGenerationRef.current += 1;
  };

  const changeField = (index: number, field: CampaignFilterField | '') => {
    markUserEdit();
    const definition = field ? campaignFilterDefinition(field) : undefined;
    const currentRule = rules[index] ?? defaultRule();
    setValue(
      `rules.${index}`,
      {
        field,
        operator: definition?.operators.includes(
          currentRule.operator as CampaignFilterFieldDefinition['operators'][number],
        )
          ? currentRule.operator
          : (definition?.operators[0] ?? ''),
        amount:
          definition?.valueKind === 'NUMBER' ||
          definition?.valueKind === 'RECENCY'
            ? 0
            : undefined,
        unit: definition?.unit === 'BYTES' ? 'MB' : undefined,
        booleanValue: undefined,
        service:
          definition?.valueKind === 'RECENCY'
            ? definition.services?.[0]
            : undefined,
        values: [],
      },
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const changeOperator = (
    index: number,
    operator: CampaignFilterFormRule['operator'],
  ) => {
    markUserEdit();
    setValue(
      `rules.${index}`,
      { ...(rules[index] ?? defaultRule()), operator },
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <section aria-label="Campaign targeting filters" className="grid gap-4">
      <div>
        <h3 className="text-lg font-semibold">Audience targeting</h3>
        <p className="text-sm text-muted-foreground">
          Every row must match (AND). Multiple values in one row are
          alternatives (OR).
        </p>
        {channels.includes('SMS') && (
          <p className="mt-1 text-sm text-muted-foreground">
            App-user targeting is unavailable when SMS is selected.
          </p>
        )}
        {channels.includes('INBOX') && (
          <p className="mt-1 text-sm text-muted-foreground">
            In-App Inbox automatically targets active, linked app-user profiles
            only; customers without an active app profile are excluded.
          </p>
        )}
      </div>
      <FieldGroup>
        {fields.map((item, index) => {
          const rule = rules[index] ?? defaultRule();
          const definition = rule.field
            ? campaignFilterDefinition(rule.field as CampaignFilterField)
            : undefined;
          const rowError = formState.errors.rules?.[index];
          const errorMessage =
            rowError?.field?.message ??
            rowError?.operator?.message ??
            rowError?.amount?.message ??
            rowError?.service?.message ??
            rowError?.booleanValue?.message ??
            rowError?.values?.message;
          return (
            <div key={item.id}>
              {index > 0 && (
                <p className="my-3 text-center text-xs font-semibold text-muted-foreground">
                  AND
                </p>
              )}
              <Field
                data-invalid={Boolean(rowError) || undefined}
                className="rounded-lg border p-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <FieldLabel htmlFor={`targeting-field-${index}`}>
                    Targeting field {index + 1}
                  </FieldLabel>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      markUserEdit();
                      remove(index);
                    }}
                    disabled={disabled}
                    aria-label={`Remove targeting rule ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
                <select
                  id={`targeting-field-${index}`}
                  aria-label={`Targeting field ${index + 1}`}
                  aria-invalid={Boolean(rowError?.field)}
                  value={rule.field}
                  onChange={(event) =>
                    changeField(
                      index,
                      event.target.value as CampaignFilterField | '',
                    )
                  }
                  disabled={disabled}
                >
                  <option value="">Choose a field</option>
                  {filterFamilies.map((family) => (
                    <optgroup key={family} label={family}>
                      {Object.entries(campaignFilterFields)
                        .filter(
                          ([, itemDefinition]) =>
                            itemDefinition.family === family,
                        )
                        .map(([field, itemDefinition]) => {
                          const blockedForSms =
                            itemDefinition.scope === 'APP_USER' &&
                            channels.includes('SMS');
                          const enforcedForInbox =
                            (field === 'has_linked_app_user' ||
                              field === 'app_user_status') &&
                            channels.includes('INBOX');
                          const blocked = blockedForSms || enforcedForInbox;
                          return (
                            <option
                              key={field}
                              value={field}
                              disabled={blocked}
                              title={
                                blockedForSms
                                  ? 'Unavailable while SMS is selected.'
                                  : enforcedForInbox
                                    ? 'In-App Inbox already requires an active, linked app-user profile.'
                                    : undefined
                              }
                            >
                              {itemDefinition.label}
                            </option>
                          );
                        })}
                    </optgroup>
                  ))}
                </select>
                {definition?.operators.length &&
                definition.operators.length > 1 ? (
                  <Field>
                    <FieldLabel htmlFor={`targeting-operator-${index}`}>
                      Targeting operator {index + 1}
                    </FieldLabel>
                    <select
                      id={`targeting-operator-${index}`}
                      aria-label={`Targeting operator ${index + 1}`}
                      aria-invalid={Boolean(rowError?.operator)}
                      value={rule.operator}
                      onChange={(event) =>
                        changeOperator(
                          index,
                          event.target
                            .value as CampaignFilterFormRule['operator'],
                        )
                      }
                      disabled={disabled}
                    >
                      {definition.operators.map((operator) => (
                        <option key={operator} value={operator}>
                          {operator === 'GTE' ? 'At least' : 'At most'}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                {definition && (
                  <RuleValueEditor
                    index={index}
                    rule={rule}
                    definition={definition}
                    setValue={setValue}
                    disabled={disabled}
                    invalid={Boolean(rowError)}
                    onUserEdit={markUserEdit}
                  />
                )}
                <FieldError>{errorMessage}</FieldError>
              </Field>
            </div>
          );
        })}
      </FieldGroup>
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          markUserEdit();
          append(defaultRule());
        }}
        disabled={disabled}
      >
        Add targeting rule
      </Button>
      {schema.safeParse({ rules }).success && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {campaignFilterSummary(toApiFilter(rules))}
        </p>
      )}
    </section>
  );
}

function formSemanticKey(filter: CampaignFilterForm): string {
  return JSON.stringify(
    filter.rules
      .map((rule) => ({
        field: rule.field,
        operator: rule.operator,
        service: rule.service,
        amount: rule.amount,
        unit: rule.unit,
        booleanValue: rule.booleanValue,
        values: [...new Set(rule.values.map((value) => value.trim()))].sort(),
      }))
      .sort((left, right) =>
        JSON.stringify(left).localeCompare(JSON.stringify(right)),
      ),
  );
}

function RuleValueEditor({
  index,
  rule,
  definition,
  setValue,
  disabled,
  invalid,
  onUserEdit,
}: {
  index: number;
  rule: CampaignFilterFormRule;
  definition: CampaignFilterFieldDefinition;
  setValue: ReturnType<typeof useForm<CampaignFilterForm>>['setValue'];
  disabled: boolean;
  invalid: boolean;
  onUserEdit: () => void;
}) {
  const setRule = (next: Partial<CampaignFilterFormRule>) => {
    onUserEdit();
    setValue(
      `rules.${index}`,
      { ...rule, ...next },
      { shouldDirty: true, shouldValidate: true },
    );
  };
  if (definition.valueKind === 'NUMBER')
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <Field data-invalid={invalid || undefined}>
          <FieldLabel htmlFor={`amount-${index}`}>
            Amount {index + 1}
          </FieldLabel>
          <Input
            id={`amount-${index}`}
            type="number"
            min={definition.minimum}
            max={definition.maximum}
            step="1"
            aria-invalid={invalid}
            value={rule.amount ?? ''}
            onChange={(event) =>
              setRule({
                amount:
                  event.target.value === ''
                    ? undefined
                    : Number(event.target.value),
              })
            }
            disabled={disabled}
          />
        </Field>
        {definition.unit === 'BYTES' && (
          <Field data-invalid={invalid || undefined}>
            <FieldLabel htmlFor={`unit-${index}`}>Unit {index + 1}</FieldLabel>
            <select
              id={`unit-${index}`}
              aria-label={`Unit ${index + 1}`}
              aria-invalid={invalid}
              value={rule.unit ?? 'MB'}
              onChange={(event) =>
                setRule({
                  unit: event.target.value as CampaignFilterFormRule['unit'],
                })
              }
              disabled={disabled}
            >
              <option value="B">B</option>
              <option value="KB">KB</option>
              <option value="MB">MB</option>
              <option value="GB">GB</option>
            </select>
          </Field>
        )}
      </div>
    );
  if (definition.valueKind === 'RECENCY')
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <Field data-invalid={invalid || undefined}>
          <FieldLabel htmlFor={`service-${index}`}>
            Service {index + 1}
          </FieldLabel>
          <select
            id={`service-${index}`}
            aria-invalid={invalid}
            value={rule.service ?? ''}
            onChange={(event) =>
              setRule({
                service: event.target
                  .value as CampaignFilterFormRule['service'],
              })
            }
            disabled={disabled}
          >
            {definition.services?.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </select>
        </Field>
        <Field data-invalid={invalid || undefined}>
          <FieldLabel htmlFor={`amount-${index}`}>Days {index + 1}</FieldLabel>
          <select
            id={`amount-${index}`}
            aria-invalid={invalid}
            value={rule.amount ?? ''}
            onChange={(event) =>
              setRule({ amount: Number(event.target.value) })
            }
            disabled={disabled}
          >
            <option value="">Choose days</option>
            {definition.values?.map((days) => (
              <option key={days} value={days}>
                {days}
              </option>
            ))}
          </select>
        </Field>
      </div>
    );
  if (definition.valueKind === 'BOOLEAN')
    return (
      <Field data-invalid={invalid || undefined}>
        <FieldLabel htmlFor={`boolean-${index}`}>Value {index + 1}</FieldLabel>
        <select
          id={`boolean-${index}`}
          aria-invalid={invalid}
          value={
            rule.booleanValue === undefined ? '' : String(rule.booleanValue)
          }
          onChange={(event) =>
            setRule({
              booleanValue:
                event.target.value === ''
                  ? undefined
                  : event.target.value === 'true',
            })
          }
          disabled={disabled}
        >
          <option value="">Choose yes or no</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </Field>
    );
  if (definition.valueKind === 'STRING')
    return (
      <Field data-invalid={invalid || undefined}>
        <FieldLabel htmlFor={`values-${index}`}>Values {index + 1}</FieldLabel>
        <Input
          id={`values-${index}`}
          aria-invalid={invalid}
          value={rule.values.join(', ')}
          onChange={(event) =>
            setRule({
              values: event.target.value
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean),
            })
          }
          placeholder="Comma-separated values"
          disabled={disabled}
        />
      </Field>
    );
  return (
    <fieldset aria-invalid={invalid} data-invalid={invalid || undefined}>
      <legend className="text-sm font-medium">Values {index + 1} (OR)</legend>
      <div className="mt-2 flex flex-wrap gap-3">
        {definition.values?.map((value) => (
          <label key={value} className="flex items-center gap-2">
            <Checkbox
              checked={rule.values.includes(value)}
              onCheckedChange={(checked) =>
                setRule({
                  values: checked
                    ? [...rule.values, value]
                    : rule.values.filter((existing) => existing !== value),
                })
              }
              disabled={disabled}
              aria-invalid={invalid}
              aria-label={value}
            />
            <span aria-hidden="true">{value}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
